/* eslint-disable @typescript-eslint/no-explicit-any */
import { IUser } from '../common/types/common';
import { API_BASE } from '../config/env';
import { httpConstants } from '../constants';
import Utils from '../utility';
import { httpService } from '../utility/httpService';

// const API_BASE = import.meta.env.VITE_ADMIN_URL;

export interface AuthResponse {
  access: string;
  refresh: string;
}

export interface TenantBasicInfo {
  id: string;
  name: string;
  tenantId: string;
  azureClientId: string;
  googleClientId: string;
}

let tenantInfoLoaded = false;
let tenantInfo: TenantBasicInfo | undefined = undefined;
let user: IUser | undefined = undefined;

export async function fetchTenantConfig(slug: string): Promise<TenantBasicInfo | undefined> {
  if (!tenantInfo || !tenantInfoLoaded) {
    let tenantData: any = null;

    const url = `${API_BASE}/api/v2/auth/tenant-test/?slug=${encodeURIComponent(slug || 'thehackettgroup')}`;
    tenantData = await httpService<TenantBasicInfo>(
      httpConstants.METHOD_TYPE.GET,
      url,
      undefined,
      undefined,
      false
    );
    if (tenantData) {
      tenantInfoLoaded = true;
    }

    tenantInfo = { ...tenantData, name: slug || 'thehackettgroup' };
  }

  return tenantInfo;
}

export async function postAzureLoginCode(
  code: string,
  redirectUri: string,
  tenantId: string
): Promise<boolean> {
  const url = `${API_BASE}/api/v2/auth/login/`;
  const payload = { code, redirect_uri: redirectUri, tenant_id: tenantId };

  const res = await httpService<AuthResponse>(
    httpConstants.METHOD_TYPE.POST,
    url,
    payload,
    undefined,
    false
  );

  if (res?.access && res?.refresh) {
    Utils.saveTokensToCookies(res.access, res.refresh);
    if (import.meta.env.DEV) {
      // console.log('Azure token exchange successful');
    }
    return true;
  }

  return false;
}

export const postGoogleLoginCode = async (
  code: string,
  redirectUri: string,
  tenantId: string
): Promise<boolean> => {
  const url = `${API_BASE}/api/v2/auth/google-login/`;
  const payload = { code, redirect_uri: redirectUri, tenant_id: tenantId };

  const res = await httpService<AuthResponse>(
    httpConstants.METHOD_TYPE.POST,
    url,
    payload,
    undefined,
    false
  );

  if (res?.access && res?.refresh) {
    Utils.saveTokensToCookies(res.access, res.refresh);
  }

  return !!res?.access;
};

export async function getUser(force = false): Promise<IUser | undefined> {
  if (force) user = undefined;

  if (!user) {
    const token = Utils.getCookie('auth-token');
    if (token) {
      try {
        const res = await httpService<IUser>(
          httpConstants.METHOD_TYPE.GET,
          `${API_BASE}/api/v2/user/`,
          undefined
        );
        user = res;

        const lastUser = localStorage.getItem('last-user');
        if (!lastUser || lastUser !== user.id) {
          localStorage.removeItem('current_organization');
          localStorage.removeItem('organizations');
        }
        localStorage.setItem('last-user', user.id || '');
      } catch {
        user = undefined;
      }
    }
  }

  return user;
}

export function signOut(): void {
  Utils.clearTokenCookies();
  localStorage.removeItem('organizations');
  tenantInfo = undefined;
  tenantInfoLoaded = false;
  user = undefined;
  const slug = localStorage.getItem('tenant-slug') || 'thehackettgroup';
  localStorage.removeItem('tenant-slug');
  localStorage.removeItem('current_organization');
  localStorage.removeItem('last-user');
  localStorage.removeItem('persist:root');
  window.location.href = `/${slug}/login`;
}

export const forgotPassword = async (username_email: string): Promise<{ valid: boolean }> => {
  return httpService<{ valid: boolean }>(
    httpConstants.METHOD_TYPE.POST,
    `${API_BASE}/api/v2/user/reset-password`,
    { username_email },
    undefined,
    false
  );
};

interface AuthTokenResponse {
  access: string;
  refresh: string;
}

export const login = async (username: string, password: string): Promise<AuthTokenResponse> => {
  return httpService<AuthTokenResponse>(
    httpConstants.METHOD_TYPE.POST,
    `${API_BASE}/api/v2/auth/`,
    { username, password },
    undefined,
    false
  );
};

const INTENDED_ROUTE_KEY = 'intendedRoute';
const INTENDED_ORG_KEY = 'intendedOrganization';

export const saveIntendedRoute = (route: string) => {
  try {
    sessionStorage.setItem(INTENDED_ROUTE_KEY, route);

    const orgId = extractOrgIdFromRoute(route);
    if (orgId) {
      sessionStorage.setItem(INTENDED_ORG_KEY, orgId);
    }
  } catch (error) {
    console.error('Failed to save intended route:', error);
  }
};

export const extractOrgIdFromRoute = (route: string): string | null => {
  try {
    const url = new URL(route, window.location.origin);
    return url.searchParams.get('orgId');
  } catch (error) {
    console.error('Error extracting org ID from route:', error);
    return null;
  }
};

// Get and clear the intended route after successful login
export const getAndClearIntendedRoute = (): string | null => {
  const route = sessionStorage.getItem(INTENDED_ROUTE_KEY);

  if (route) {
    sessionStorage.removeItem(INTENDED_ROUTE_KEY);
  }

  return route;
};

// Clear all intended route data
export const clearIntendedRoute = () => {
  sessionStorage.removeItem(INTENDED_ROUTE_KEY);
  sessionStorage.removeItem(INTENDED_ORG_KEY);
};

// Enhanced login function that returns the redirect URL and organization info
export const handleSuccessfulLogin = (): string => {
  const intendedRoute = getAndClearIntendedRoute();
  const redirectUrl = intendedRoute || '/';

  return redirectUrl;
};
